import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DynamicFormModel,
  DynamicFormService,
  DynamicInputModel,
  DynamicSelectModel,
  DynamicTextAreaModel,
} from '@ng-dynamic-forms/core';
import {
  AuthService,
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  I18nModule,
  LanguageService,
  TranslationService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService, GenericLinkModule, OutletRefModule } from '@spartacus/storefront';
import * as includes from 'lodash';
import { Observable, Subscription, combineLatest, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { B2bUserService } from 'src/app/service/b2buser/b2b-user.service';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { ComponentService } from 'src/app/service/component.service';
import { ContactUsService, ContactUsWsDTO } from 'src/app/service/user/contact-us.service';
import { CustomerServiceSectionModule } from '../customer-service-section/customer-service-section.module';
import { AccordionModule } from '../../accordion/accordion.module';
import { DynamicFormsModule } from '../../dynamic-forms/dynamic-forms.module';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-customer-service-content',
  templateUrl: './customer-service-content.component.html',
  styleUrls: ['./customer-service-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AsyncPipe],
  imports: [
    CommonModule,
    OutletRefModule,
    CustomerServiceSectionModule,
    AccordionModule,
    DynamicFormsModule,
    GenericLinkModule,
    I18nModule,
    NgbDropdownModule,
    MatListModule,
    MatIconModule,
    RouterModule,
  ],
  standalone: true,
})
export class CustomerServiceContentComponent implements OnInit, AfterContentChecked, OnDestroy {
  customerServiceContentData$: Observable<any>;
  lodash = includes;
  isUserLoggedIn = false;
  onSubmitContactUsFormInProgress = false;

  contactUsFormGroup: FormGroup;
  contactUsFormModel: DynamicFormModel;
  contactUsFormModelSubscription$: Subscription;
  contactUsFormGroupSubscription$: Subscription;
  currentPageSubscription$: Subscription;

  firstNameInput: DynamicInputModel;
  lastNameInput: DynamicInputModel;
  emailAddressInput: DynamicInputModel;
  contactNumberInput: DynamicInputModel;
  requestTypeSelect: DynamicSelectModel<string>;
  messageInput: DynamicTextAreaModel;

  pointsOfService: any[] = [];

  isMobile = false;
  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);

  customerServiceSectionFaq$: Observable<any>;
  customerServiceSectionLinks$: Observable<any>;
  customerServiceSectionContactUs$: Observable<any>;
  customerServiceSectionContactUsContent$: Observable<any>;
  customerServiceSectionList: any[] = [];

  activeI = 0;
  customerServiceSectionFaqData$: Observable<any>;
  customerServiceSectionLinksData$: Observable<any>;
  customerServiceSectionContactUsData$: Observable<any>;
  customerServiceSectionContactUsContentData$: Observable<any>;

  customerServiceSectionFaqAccordionData$: Observable<any>;

  shimanoUserData: any;
  errorMessages: any = [];
  formFieldId: any = [];
  inCustomerServicePage: boolean = false;

  constructor(
    private cmsService: CmsService,
    private componentService: ComponentService,
    private b2bUserService: B2bUserService,
    private formService: DynamicFormService,
    private baseStoreService: BaseStoreService,
    private breakpointService: BreakpointService,
    private translation: TranslationService,
    private contactUsService: ContactUsService,
    private messageService: GlobalMessageService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private languageService: LanguageService,
    private pipeAsync: AsyncPipe
  ) {
    // DO NOT CHANGE THE ORDER
    this.formFieldId = [
      'firstName_medium',
      'lastName_medium',
      'emailAddress_full',
      'emailAddress_medium',
      'contactNumber',
      'contactNumberInvalid',
      'requestType',
      'message',
    ];
  }

  ngOnInit() {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
    this.setCustomerServiceContentData();

    this.currentPageSubscription$ = this.cmsService
      .getCurrentPage()
      .pipe(
        filter(Boolean),
        tap((page) => {
          this.inCustomerServicePage = page?.label?.includes('/customer-service') ?? false;

          // For Mobile: Only the first 3. where 1st is for FAQ, 2nd is for Links and 3rd is for Contact Us
          if (this.inCustomerServicePage) {
            const components = page.slots.CustomerServiceContent.components.slice(0, 3);
            this.getCustomerServiceSection(components);
          }
          this.cdr.detectChanges();
        }),
        switchMap((page) => {
          if (this.inCustomerServicePage && page.template === 'ShimanoCustomerServicePageTemplate') {
            return combineLatest([
              this.componentService.dynamicFormModel$,
              this.componentService.dynamicFormGroup$,
              this.baseStoreService.getBasestoreData(),
            ]).pipe(
              tap(([formModel, formGroup, baseStoreData]) => {
                this.updateFormModel(formModel);
                this.updateFormGroup(formGroup);
                this.getUserDetails();

                if (baseStoreData) {
                  this.getPointsOfService(baseStoreData);
                }
              }),
              map(() => true)
            );
          }
          return of(false);
        })
      )
      .subscribe();

    this.authService
      .isUserLoggedIn()
      .pipe(
        distinctUntilChanged(),
        map((isUserLoggedIn) => {
          this.isUserLoggedIn = isUserLoggedIn;
          return isUserLoggedIn;
        }),
        mergeMap((isUserLoggedIn) => iif(() => isUserLoggedIn, this.baseStoreService.getBasestoreData(), of(null)))
      )
      .subscribe((baseStoreData) => {
        if (baseStoreData) {
          this.getPointsOfService(baseStoreData);
        }
      });
  }

  setCustomerServiceContentData() {
    this.customerServiceContentData$ = this.componentService.customerServiceContent$.pipe(
      filter(Boolean),
      switchMap((data) =>
        combineLatest(
          data?.contents
            .split(' ')
            .map((uid: string) => this.cmsService.getComponentData<any>(uid).pipe(map((d) => ({ ...d }))))
        )
      )
    );
  }

  getCustomerServiceSectionData(customerServiceSection$: Observable<any>): Observable<any> {
    return customerServiceSection$.pipe(
      switchMap((data) =>
        combineLatest(
          data.contents
            .split(' ')
            .map((uid: string) => this.cmsService.getComponentData<any>(uid).pipe(map((d) => ({ ...d }))))
        )
      )
    );
  }

  getUserDetails() {
    this.b2bUserService
      .getB2bUserProfile()
      .pipe(take(1))
      .subscribe((userData) => {
        let contactNumber: string;
        if (userData.defaultBillingAddress) {
          contactNumber = userData.defaultBillingAddress.cellphone
            ? userData.defaultBillingAddress.cellphone
            : userData.phone;
        }
        this.setContactUsFormInitValues(userData.firstName, userData.lastName, userData.email, contactNumber);
      });
  }

  getTranslatedError(id: string): Observable<string> {
    return this.translation.translate(`error.dynamicform.${id}`);
  }

  updateFormModel(formModel: any) {
    if (this.inCustomerServicePage === true) {
      this.contactUsFormModel = formModel;
      // First Name
      this.firstNameInput = this.formService.findModelById<DynamicInputModel>(
        'firstName_medium',
        this.contactUsFormModel
      );
      if (this.firstNameInput) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[0])),
        };
        this.firstNameInput.required = true;
        this.firstNameInput.errorMessages = hasError;
      }

      // Last Name
      this.lastNameInput = this.formService.findModelById<DynamicInputModel>(
        'lastName_medium',
        this.contactUsFormModel
      );
      if (this.lastNameInput) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[1])),
        };
        if (this.inCustomerServicePage === true) this.lastNameInput.required = true;
        else this.lastNameInput.required = false;
        this.lastNameInput.errorMessages = hasError;
      }

      // Email
      this.emailAddressInput = this.formService.findModelById<DynamicInputModel>(
        'emailAddress_medium',
        this.contactUsFormModel
      );
      if (this.emailAddressInput) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[2])),
          pattern: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[3])),
        };
        this.emailAddressInput.required = true;
        this.emailAddressInput.errorMessages = hasError;
      }

      // Contact Number
      this.contactNumberInput = this.formService.findModelById<DynamicInputModel>(
        'contactNumberNoPrefix',
        this.contactUsFormModel
      );
      if (this.contactNumberInput !== null) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[4])),
          pattern: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[5])),
        };
        this.contactNumberInput.required = true;
        this.contactNumberInput.errorMessages = hasError;
      }

      // Request Type
      this.requestTypeSelect = this.formService.findModelById<DynamicSelectModel<string>>(
        'requestType',
        this.contactUsFormModel
      );
      if (this.requestTypeSelect !== null) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[6])),
        };
        this.requestTypeSelect.required = true;
        this.requestTypeSelect.errorMessages = hasError;
      }

      // Message
      this.messageInput = this.formService.findModelById<DynamicTextAreaModel>('message', this.contactUsFormModel);
      if (this.messageInput !== null) {
        const hasError = {
          required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[7])),
        };
        this.messageInput.required = true;
        this.messageInput.errorMessages = hasError;
      }
    } else if (formModel && this.inCustomerServicePage === false) {
      this.contactUsFormGroup.reset();
    }
    this.formService.detectChanges();
  }

  setContactUsFormInitValues(firstName: string, lastName: string, email: string, contactNumber: string) {
    if (this.firstNameInput) {
      this.firstNameInput.value = firstName;
    }
    if (this.lastNameInput) {
      this.lastNameInput.value = lastName;
    }
    if (this.emailAddressInput) {
      this.emailAddressInput.value = email;
    }
    if (this.contactNumberInput) {
      this.contactNumberInput.value = contactNumber;
    }
  }

  updateFormGroup(formGroup: any): void {
    this.contactUsFormGroup = formGroup;
  }

  getPointsOfService(baseStoreData: any) {
    this.pointsOfService = baseStoreData.pointsOfService?.filter((location: any) => location.address);
  }

  getCustomerServiceSection(components: any) {
    this.customerServiceSectionFaq$ = this.cmsService.getComponentData(components[0].uid);
    this.customerServiceSectionLinks$ = this.cmsService.getComponentData(components[1].uid);
    this.customerServiceSectionContactUs$ = this.cmsService.getComponentData(components[2].uid);
    this.customerServiceSectionContactUs$.pipe(take(1)).subscribe((data) => {
      this.customerServiceSectionContactUsContent$ = this.cmsService.getComponentData(data.contents);
      this.customerServiceSectionContactUsContentData$ = this.getCustomerServiceSectionData(
        this.customerServiceSectionContactUsContent$
      );
    });

    this.customerServiceSectionFaqData$ = this.getCustomerServiceSectionData(this.customerServiceSectionFaq$);
    this.customerServiceSectionFaqData$.pipe(take(1)).subscribe((faq) => {
      this.onSelectFaqData(faq[0], 0); // select first index
    });
    this.customerServiceSectionLinksData$ = this.getCustomerServiceSectionData(this.customerServiceSectionLinks$);
    this.customerServiceSectionContactUsData$ = this.getCustomerServiceSectionData(
      this.customerServiceSectionContactUs$
    );
  }

  onSelectFaqData(faqGroup: any, index: any) {
    this.activeI = index;
    const faqGroupData$ = this.cmsService.getComponentData(faqGroup.uid);
    this.customerServiceSectionFaqAccordionData$ = this.getCustomerServiceSectionData(faqGroupData$);
  }

  onSubmitContactUsForm() {
    if (!this.onSubmitContactUsFormInProgress) {
      this.onSubmitContactUsFormInProgress = true;
      const contactUsFormDetails: ContactUsWsDTO = {
        firstName: this.firstNameInput.value.toString(),
        lastName: this.lastNameInput.value.toString(),
        email: this.emailAddressInput.value.toString(),
        contactNumber: this.contactNumberInput.value.toString(),
        requestType: this.requestTypeSelect.value.toString(),
        message: this.messageInput.value.toString(),
      };

      const SUCCESS_200 = 200;
      const SUCCESS_202 = 202;

      this.contactUsService.postContactUsForm(contactUsFormDetails).subscribe(
        (success) => {
          if (success.status === SUCCESS_200 || success.status === SUCCESS_202) {
            this.showGlobalMessage(
              'customerService.contactUsFormSubmitSuccess',
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
          }
          setTimeout(() => {
            this.onSubmitContactUsFormInProgress = false;
          }, 2000);
        },
        (error) => {
          this.onSubmitContactUsFormInProgress = false;
          // catch error
        }
      );
    }
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  private showGlobalMessage(text: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text }, messageType);
    const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
    element.scrollIntoView();
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.contactUsFormModelSubscription$) {
      this.contactUsFormModelSubscription$.unsubscribe();
    }
    if (this.contactUsFormGroupSubscription$) {
      this.contactUsFormGroupSubscription$.unsubscribe();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
  }
}
